import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { ArrowLeft, Coins, Hammer, Lock, ArrowUp } from "lucide-react";
import ArcadeNav from "../../arcade/ArcadeNav";
import ArcadeFooter from "../../arcade/ArcadeFooter";
import { useAuth } from "../../contexts/AuthContext";
import { usePresence } from "../../game/presence";
import useSEO from "../../seo/useSEO";
import { SEO_GAMES } from "../../seo/seoData";
import GameSEOContent from "../../seo/GameSEOContent";
import {
  loadSave, persistSave, applyTick, applyOfflineGain,
  totalIncomePerSec, upgradeCost, isUnlocked,
  tryUpgrade, tapCollect, fmtGold, fmtRate,
} from "./TycoonEngine";
import {
  BUILDINGS, BUILDING_BY_ID, COIN_MILESTONES, BUILD_FIRST_COINS, TAP_BONUS_SEC,
} from "./buildings";
import "./tycoon.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TICK_MS = 250;          // UI refresh cadence — engine accumulates real time, not "ticks"
const PERSIST_EVERY_MS = 5000;

export default function TycoonScreen() {
  useSEO(SEO_GAMES["zombie-tycoon"]);
  const { user, refresh } = useAuth();
  usePresence(true, "zombie-tycoon");

  const [save, setSave] = useState(() => loadSave());
  const [offlineGain, setOfflineGain] = useState(null);   // {secs, gain}
  const [tappedId, setTappedId] = useState(null);
  const [floaters, setFloaters] = useState({});           // { buildingId: text }
  const tappedTimer = useRef(null);
  const lastPersist = useRef(Date.now());

  // ---------- Offline catch-up on first mount -----------------------------
  useEffect(() => {
    const fresh = loadSave();
    const { next, secs, gain } = applyOfflineGain(fresh, Date.now());
    if (secs > 30 && gain > 0) {
      setOfflineGain({ secs, gain });
    }
    setSave(next);
  }, []);

  // ---------- Tick loop (every 250ms) -------------------------------------
  useEffect(() => {
    const id = setInterval(() => {
      setSave((prev) => {
        const now = Date.now();
        const next = applyTick(prev, now);
        if (now - lastPersist.current > PERSIST_EVERY_MS) {
          persistSave(next);
          lastPersist.current = now;
        }
        return next;
      });
    }, TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Persist on page-hide so cmd-W doesn't drop the last few seconds.
  useEffect(() => {
    const onHide = () => persistSave(save);
    window.addEventListener("beforeunload", onHide);
    window.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("beforeunload", onHide);
      window.removeEventListener("visibilitychange", onHide);
    };
  }, [save]);

  // ---------- Arcade-coin milestones --------------------------------------
  const lastClaimedRef = useRef({ firstBuild: new Set(save.firstBuildClaimed || []), milestones: new Set(save.milestonesClaimed || []) });

  const claimArcadeCoins = useCallback(async (amount, reason) => {
    if (!user || amount <= 0) return;
    try {
      const res = await axios.post(`${API}/games/tycoon/reward`, { amount, reason });
      if (res.data?.coins_awarded > 0) {
        toast.success(`+${res.data.coins_awarded} Arcade Coins`, { description: reason, duration: 3500 });
        await refresh();
      }
    } catch (_e) { /* silent */ }
  }, [user, refresh]);

  // Watch lifetime gold for milestone payouts.
  useEffect(() => {
    const life = save.lifetimeGold || 0;
    let dirty = false;
    const claimed = new Set(save.milestonesClaimed || []);
    for (const m of COIN_MILESTONES) {
      if (life >= m.gold && !claimed.has(m.gold)) {
        claimed.add(m.gold);
        dirty = true;
        claimArcadeCoins(m.coins, `Lifetime Gold ≥ ${fmtGold(m.gold)}`);
      }
    }
    if (dirty) {
      setSave((s) => ({ ...s, milestonesClaimed: Array.from(claimed) }));
    }
    lastClaimedRef.current.milestones = claimed;
  }, [save.lifetimeGold, save.milestonesClaimed, claimArcadeCoins]);

  // ---------- Actions -----------------------------------------------------
  const handleTap = useCallback((bld) => {
    const lvl = save.levels[bld.id] || 0;
    if (lvl <= 0) return;
    setSave((s) => tapCollect(s, bld.id));
    // Visual feedback.
    setTappedId(bld.id);
    if (tappedTimer.current) clearTimeout(tappedTimer.current);
    tappedTimer.current = setTimeout(() => setTappedId(null), 220);
    // Floater showing the bonus number.
    const bonus = (BUILDING_BY_ID[bld.id].baseIncome * Math.pow(1.10, lvl - 1)) * TAP_BONUS_SEC;
    setFloaters((f) => ({ ...f, [bld.id]: `+${fmtGold(bonus)}` }));
    setTimeout(() => setFloaters((f) => { const c = { ...f }; delete c[bld.id]; return c; }), 800);
  }, [save.levels]);

  const handleBuyOrUpgrade = useCallback((bld, e) => {
    if (e) e.stopPropagation();
    const lvl = save.levels[bld.id] || 0;
    const cost = upgradeCost(bld.id, lvl);
    if (save.gold < cost) {
      toast.error("Not enough gold");
      return;
    }
    const next = tryUpgrade(save, bld.id);
    if (!next) return;
    setSave(next);
    persistSave(next);
    const wasFirstBuild = lvl === 0;
    if (wasFirstBuild) {
      // Pay arcade coins for first-build (once per building).
      const claimed = new Set(save.firstBuildClaimed || []);
      if (!claimed.has(bld.id)) {
        claimed.add(bld.id);
        setSave((s) => ({ ...s, firstBuildClaimed: Array.from(claimed) }));
        claimArcadeCoins(BUILD_FIRST_COINS, `Built ${bld.name}`);
      }
      toast.success(`${bld.name} built!`);
    }
  }, [save, claimArcadeCoins]);

  // ---------- Derived numbers --------------------------------------------
  const incomePerSec = useMemo(() => totalIncomePerSec(save), [save]);

  return (
    <div className="tycoon-page" data-testid="tycoon-page">
      <Toaster position="top-center" theme="dark" />
      <ArcadeNav />

      <main className="tycoon-main">
        <Link to="/" className="tycoon-back" data-testid="tycoon-back-to-arcade">
          <ArrowLeft size={16} /> Back to Arcade
        </Link>

        <section className="tycoon-stats" data-testid="tycoon-stats">
          <div className="tycoon-stat">
            <span className="tycoon-stat-label">Gold</span>
            <span className="tycoon-stat-value" data-testid="tycoon-gold">{fmtGold(save.gold)}</span>
          </div>
          <div className="tycoon-stat">
            <span className="tycoon-stat-label">Income</span>
            <span className="tycoon-stat-value rate" data-testid="tycoon-rate">{fmtRate(incomePerSec)}</span>
          </div>
          <div className="tycoon-stat">
            <span className="tycoon-stat-label">Lifetime</span>
            <span className="tycoon-stat-value" data-testid="tycoon-lifetime">{fmtGold(save.lifetimeGold || 0)}</span>
          </div>
          <div className="tycoon-hint">
            <Coins size={14} /> Tap a building for a +{TAP_BONUS_SEC}s bonus
          </div>
        </section>

        <div className="tycoon-grid" data-testid="tycoon-grid">
          {BUILDINGS.map((b) => {
            const lvl = save.levels[b.id] || 0;
            const built = lvl > 0;
            const unlocked = isUnlocked(save, b.id);
            const cost = upgradeCost(b.id, lvl);
            const canAfford = save.gold >= cost;
            const klass = built ? "built" : unlocked ? "unlocked-unbuilt" : "locked";
            return (
              <article
                key={b.id}
                className={`tycoon-card ${klass} ${tappedId === b.id ? "tapped" : ""}`}
                data-testid={`tycoon-card-${b.id}`}
                onClick={() => built ? handleTap(b) : (unlocked && canAfford ? handleBuyOrUpgrade(b) : null)}
              >
                <div className="tycoon-art">
                  <img src={b.sprite} alt={b.name} loading="lazy" draggable={false} />
                </div>
                <div className="tycoon-title-row">
                  <span className="tycoon-name">{b.name}</span>
                  <span className="tycoon-level">{built ? `Lv ${lvl}` : unlocked ? "Ready" : "Locked"}</span>
                </div>
                {built && (
                  <div className="tycoon-row">
                    <span>Income</span>
                    <span className="rate">{fmtRate(b.baseIncome * Math.pow(1.10, lvl - 1))}</span>
                  </div>
                )}
                {!built && unlocked && (
                  <div className="tycoon-row">
                    <span>First income</span>
                    <span className="rate">{fmtRate(b.baseIncome)}</span>
                  </div>
                )}
                {unlocked ? (
                  <button
                    type="button"
                    className={built ? "tycoon-upgrade-btn" : "tycoon-build-btn"}
                    onClick={(e) => handleBuyOrUpgrade(b, e)}
                    disabled={!canAfford}
                    data-testid={`tycoon-${built ? "upgrade" : "build"}-${b.id}`}
                  >
                    {built ? <><ArrowUp size={14} /> Upgrade · {fmtGold(cost)}</>
                           : <><Hammer size={14} /> Build · {fmtGold(cost)}</>}
                  </button>
                ) : (
                  <div className="tycoon-lock-line">
                    <Lock size={12} /> Unlocks at {fmtGold(b.unlockGold)} lifetime gold
                  </div>
                )}
                {floaters[b.id] && <span className="floater">{floaters[b.id]}</span>}
              </article>
            );
          })}
        </div>

        {offlineGain && (
          <div className="tycoon-offline" onClick={() => setOfflineGain(null)} data-testid="tycoon-offline-modal">
            <div className="tycoon-offline-card" onClick={(e) => e.stopPropagation()}>
              <h3>Welcome back, Commander</h3>
              <p>Your zombie workforce kept earning while you were away…</p>
              <div className="gain">+{fmtGold(offlineGain.gain)} gold</div>
              <p>({Math.round(offlineGain.secs / 60)} minutes of offline production, at 50% efficiency)</p>
              <button type="button" onClick={() => setOfflineGain(null)} data-testid="tycoon-offline-claim">Sweet, let&apos;s play</button>
            </div>
          </div>
        )}

        <GameSEOContent gameId="zombie-tycoon" />
        <ArcadeFooter />
      </main>
    </div>
  );
}
